// builderLayoutsSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

type LayoutJSON = string; // JSON.stringify(SerializedNodes)

export interface BuilderLayoutEntry {
  formLayout: LayoutJSON | null;
  outputLayout: LayoutJSON | null;
  // optional metadata
  reportName?: string | null;
  documentType?: string | null;
  updatedAt?: number; // epoch ms
}

interface BuilderLayoutsState {
  // New multi-layout store
  byId: Record<string, BuilderLayoutEntry>;
  // Legacy singletons (kept for backward compatibility with old code paths)
  formLayout: LayoutJSON | null;
  outputLayout: LayoutJSON | null;
}

const initialState: BuilderLayoutsState = {
  byId: {},
  formLayout: null,
  outputLayout: null,
};

const toStringLayout = (data: any): LayoutJSON =>
  typeof data === 'string' ? data : JSON.stringify(data);

type IdPayload<T = unknown> = {
  reportLayoutId: string;
  data?: T;
};

const touch = (e: BuilderLayoutEntry) => {
  e.updatedAt = Date.now();
};

const ensureEntry = (
  state: BuilderLayoutsState,
  id: string
): BuilderLayoutEntry => {
  if (!state.byId[id]) {
    state.byId[id] = {
      formLayout: null,
      outputLayout: null,
      reportName: null,
      documentType: null,
      updatedAt: Date.now(),
    };
  }
  return state.byId[id];
};

const builderLayoutsSlice = createSlice({
  name: 'builderLayouts',
  initialState,
  reducers: {
    // ---------- Legacy (keep working) ----------
    setFormLayout(state, action: PayloadAction<string | object | null>) {
      state.formLayout = action.payload ? toStringLayout(action.payload) : null;
    },
    setOutputLayout(state, action: PayloadAction<string | object | null>) {
      state.outputLayout = action.payload
        ? toStringLayout(action.payload)
        : null;
    },
    ensureOutputFromForm(state) {
      if (!state.outputLayout && state.formLayout) {
        state.outputLayout = state.formLayout; // strings are immutable
      }
    },
    clearLayouts(state) {
      state.formLayout = null;
      state.outputLayout = null;
    },

    // ---------- Multi-ID APIs ----------
    setFormLayoutById(
      state,
      action: PayloadAction<IdPayload<string | object | null>>
    ) {
      const { reportLayoutId, data } = action.payload;
      const entry = ensureEntry(state, reportLayoutId);
      entry.formLayout = data ? toStringLayout(data) : null;
      touch(entry);
    },

    setOutputLayoutById(
      state,
      action: PayloadAction<IdPayload<string | object | null>>
    ) {
      const { reportLayoutId, data } = action.payload;
      const entry = ensureEntry(state, reportLayoutId);
      entry.outputLayout = data ? toStringLayout(data) : null;
      touch(entry);
    },

    ensureOutputFromFormById(state, action: PayloadAction<IdPayload>) {
      const { reportLayoutId } = action.payload;
      const entry = ensureEntry(state, reportLayoutId);
      if (!entry.outputLayout && entry.formLayout) {
        entry.outputLayout = entry.formLayout;
        touch(entry);
      }
    },

    // Optionally clone form → output explicitly
    cloneFormToOutputById(state, action: PayloadAction<IdPayload>) {
      const { reportLayoutId } = action.payload;
      const entry = ensureEntry(state, reportLayoutId);
      if (entry.formLayout) {
        entry.outputLayout = entry.formLayout;
        touch(entry);
      }
    },

    // Optional metadata (nice to have)
    setLayoutMetaById(
      state,
      action: PayloadAction<
        IdPayload<{ reportName?: string | null; documentType?: string | null }>
      >
    ) {
      const { reportLayoutId, data } = action.payload;
      const entry = ensureEntry(state, reportLayoutId);
      if (data) {
        if (data.reportName !== undefined) entry.reportName = data.reportName;
        if (data.documentType !== undefined)
          entry.documentType = data.documentType;
      }
      touch(entry);
    },

    deleteLayoutById(state, action: PayloadAction<IdPayload>) {
      const { reportLayoutId } = action.payload;
      delete state.byId[reportLayoutId];
    },

    clearAllById(state) {
      state.byId = {};
    },
  },
});

export const {
  // legacy
  setFormLayout,
  setOutputLayout,
  ensureOutputFromForm,
  clearLayouts,
  // multi-id
  setFormLayoutById,
  setOutputLayoutById,
  ensureOutputFromFormById,
  cloneFormToOutputById,
  setLayoutMetaById,
  deleteLayoutById,
  clearAllById,
} = builderLayoutsSlice.actions;

export default builderLayoutsSlice.reducer;

// ---------- Selectors (null-safe) ----------

// Legacy (still work)
export const selectFormLayout = (s: any): LayoutJSON | null =>
  s?.builderLayouts?.formLayout ?? null;

export const selectOutputLayout = (s: any): LayoutJSON | null =>
  s?.builderLayouts?.outputLayout ?? null;

// New: get whole entry by id
export const selectLayoutEntryById = (
  s: any,
  reportLayoutId?: string
): BuilderLayoutEntry | null => {
  if (!reportLayoutId) return null;
  return s?.builderLayouts?.byId?.[reportLayoutId] ?? null;
};

// New: the selector you asked for
export const selectOutputLayoutById = (
  s: any,
  reportLayoutId?: string
): LayoutJSON | null => {
  if (!reportLayoutId) return null;

  const byId = s?.builderLayouts?.byId;
  if (byId && byId[reportLayoutId]?.outputLayout) {
    return byId[reportLayoutId].outputLayout;
  }

  // Back-compat: if not found, fall back to legacy singleton
  return s?.builderLayouts?.outputLayout ?? null;
};

// Optional convenience:
export const selectFormLayoutById = (
  s: any,
  reportLayoutId?: string
): LayoutJSON | null => {
  if (!reportLayoutId) return null;
  const byId = s?.builderLayouts?.byId;
  if (byId && byId[reportLayoutId]?.formLayout) {
    return byId[reportLayoutId].formLayout;
  }
  return s?.builderLayouts?.formLayout ?? null;
};
